import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '@/services/auth';
import { useAdminAuth } from './useAdminAuth';
import { createLogger } from '@/utils/logger';

const logger = createLogger('useSessionMonitor');

interface SessionMonitorState {
  isModalOpen: boolean;
  sessionState: 'warning' | 'final-warning' | 'expired';
  timeRemaining: number;
  isExtending: boolean;
}

interface SessionWarningThresholds {
  warning: number;      // 30 minutes
  finalWarning: number; // 2 minutes  
  expired: number;      // 0 minutes
}

// Move thresholds outside component to prevent recreation on every render
const THRESHOLDS: SessionWarningThresholds = {
  warning: 30 * 60 * 1000,      // 30 minutes
  finalWarning: 2 * 60 * 1000,  // 2 minutes
  expired: 0,                   // 0 minutes
};

const DEFAULT_STATE: SessionMonitorState = {
  isModalOpen: false,
  sessionState: 'warning',
  timeRemaining: 0,
  isExtending: false,
};

export const useSessionMonitor = () => {
  const { isAuthenticated, signOut } = useAdminAuth();
  const [state, setState] = useState<SessionMonitorState>(DEFAULT_STATE);

  // Track which warnings have been shown to avoid duplicates
  const warningsShownRef = useRef<Set<string>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastCheckRef = useRef<number>(0);


  /**
   * Reset warning state (when session is extended)
   */
  const resetWarnings = useCallback(() => {
    warningsShownRef.current.clear();
    setState(prev => ({ ...prev, isModalOpen: false }));
    logger.info('Session warnings reset');
  }, []);

  /**
   * Check session and show appropriate warnings
   */
  const checkSession = useCallback(() => {
    if (!isAuthenticated) {
      return;
    }

    const timeRemaining = authService.getSessionTimeRemaining();
    const now = Date.now();
    
    // Avoid checking too frequently
    if (now - lastCheckRef.current < 30000) { // 30 seconds minimum between checks
      return;
    }
    lastCheckRef.current = now;

    // Only log when approaching expiration (less than 10 minutes remaining)
    if (timeRemaining < 10 * 60 * 1000) {
      logger.debug('Checking session:', { 
        timeRemaining: Math.floor(timeRemaining / 1000), 
        warningsShown: Array.from(warningsShownRef.current) 
      });
    }

    // Session expired
    if (timeRemaining <= THRESHOLDS.expired) {
      if (!warningsShownRef.current.has('expired')) {
        logger.warn('Session expired, showing expired modal');
        warningsShownRef.current.add('expired');
        setState({
          isModalOpen: true,
          sessionState: 'expired',
          timeRemaining: 0,
          isExtending: false,
        });
      }
      return;
    }

    // Final warning (2 minutes)
    if (timeRemaining <= THRESHOLDS.finalWarning) {
      if (!warningsShownRef.current.has('final-warning')) {
        logger.warn('Session final warning, showing final warning modal');
        warningsShownRef.current.add('final-warning');
        setState({
          isModalOpen: true,
          sessionState: 'final-warning',
          timeRemaining,
          isExtending: false,
        });
      } else {
        // Update countdown if modal is already open for final warning
        setState(prev => prev.isModalOpen && prev.sessionState === 'final-warning' 
          ? { ...prev, timeRemaining } 
          : prev
        );
      }
      return;
    }

    // Warning (30 minutes)
    if (timeRemaining <= THRESHOLDS.warning) {
      if (!warningsShownRef.current.has('warning')) {
        logger.info('Session warning, showing warning modal');
        warningsShownRef.current.add('warning');
        setState({
          isModalOpen: true,
          sessionState: 'warning',
          timeRemaining,
          isExtending: false,
        });
      } else {
        // Update countdown if modal is already open for warning
        setState(prev => prev.isModalOpen && prev.sessionState === 'warning' 
          ? { ...prev, timeRemaining } 
          : prev
        );
      }
      return;
    }

    // No warnings needed, but update time if modal is open
    setState(prev => prev.isModalOpen ? { ...prev, timeRemaining } : prev);
  }, [isAuthenticated]);

  /**
   * Extend the current session
   */
  const extendSession = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isExtending: true }));
      
      const success = await authService.extendSession();
      
      if (success) {
        logger.info('Session extended successfully');
        resetWarnings();
        
        // Close modal after successful extension
        setState({
          isModalOpen: false,
          sessionState: 'warning',
          timeRemaining: authService.getSessionTimeRemaining(),
          isExtending: false,
        });
      } else {
        logger.error('Failed to extend session - session may have already expired');
        // Let the next check handle showing expired state
        setState(prev => ({ ...prev, isExtending: false }));
      }
    } catch (error) {
      logger.error('Error extending session:', error);
      setState(prev => ({ ...prev, isExtending: false }));
    }
  }, [resetWarnings]);

  /**
   * Close the modal (for dismissible warnings)
   */
  const closeModal = useCallback(() => {
    setState(prev => ({ ...prev, isModalOpen: false }));
  }, []);

  /**
   * Handle sign out
   */
  const handleSignOut = useCallback(() => {
    logger.info('User signed out from session monitor');
    signOut();
  }, [signOut]);

  /**
   * Start session monitoring
   */
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear any existing intervals and state when not authenticated
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
      warningsShownRef.current.clear();
      return;
    }

    // Initial check on the next tick to avoid synchronous state updates in the effect body
    const initialCheckTimeout = window.setTimeout(() => {
      checkSession();
    }, 0);

    // Set up interval for regular checks
    // Check more frequently as we get closer to expiration
    const getCheckInterval = () => {
      const timeRemaining = authService.getSessionTimeRemaining();
      if (timeRemaining <= THRESHOLDS.finalWarning) {
        return 10000; // 10 seconds when in final warning
      } else if (timeRemaining <= THRESHOLDS.warning) {
        return 30000; // 30 seconds when in warning period
      } else {
        return 60000; // 1 minute for normal monitoring
      }
    };

    const scheduleNextCheck = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      intervalRef.current = setInterval(() => {
        checkSession();
        // Reschedule with appropriate interval
        scheduleNextCheck();
      }, getCheckInterval());
    };

    scheduleNextCheck();

    // Cleanup on unmount
    return () => {
      clearTimeout(initialCheckTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [isAuthenticated, checkSession]);

  /**
   * Listen for session changes to reset warnings when session is refreshed
   */
  useEffect(() => {
    const handleAuthChange = () => {
      if (isAuthenticated) {
        // Reset warnings when auth state changes (e.g., session refreshed)
        const currentTime = authService.getSessionTimeRemaining();
        if (currentTime > THRESHOLDS.warning) {
          resetWarnings();
        }
      }
    };

    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, [isAuthenticated, resetWarnings]);

  /**
   * Handle visibility change to check session when user returns to tab
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        // Reset the last check time to force immediate check when tab becomes visible
        lastCheckRef.current = 0;
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, checkSession]);

  const visibleState = isAuthenticated ? state : DEFAULT_STATE;

  return {
    // Modal state
    isModalOpen: visibleState.isModalOpen,
    sessionState: visibleState.sessionState,
    timeRemaining: visibleState.timeRemaining,
    isExtending: visibleState.isExtending,
    
    // Actions
    extendSession,
    closeModal,
    signOut: handleSignOut,
    
    // Utils
    resetWarnings,
  };
};

export default useSessionMonitor;
